/*
 * A dialect is everything about "what this text is": the document model, the markdown that becomes
 * it and the markdown it becomes again. The editor around it — input rules, commands, the clipboard,
 * the React binding — knows only this interface, so a second kind of text beside a chat message is a
 * second configuration and not a second editor.
 *
 * A configuration is data, with two exceptions that cannot be: the serializer is a function of the
 * schema, because its writers need the node types the schema has just made, and the blocks markdown
 * keeps on one line are named as strings and resolved here for the same reason.
 *
 * What a dialect may differ in is bounded on purpose. The shared node and mark specs
 * (`nodes.ts`, `marks.ts`), the tokenizer (`tokenizer.ts`) and the shared writers (`serialize.ts`)
 * are built from options; a dialect that needs a spec none of them can describe writes that one
 * spec and keeps the rest. Nothing in this directory knows which dialects exist.
 */

import type MarkdownIt from "markdown-it"
import { MarkdownParser, type ParseSpec } from "prosemirror-markdown"
import { Schema, type MarkSpec, type Node, type NodeSpec, type NodeType } from "prosemirror-model"
import { nodeType } from "../schemaTypes.ts"
import { buildSerializer, type SerializerSpec } from "./serialize.ts"
import { buildTokenizer, type TokenizerConfig } from "./tokenizer.ts"

export interface DialectConfig {
	/** What this dialect is called, in an error a developer reads. */
	name: string
	/** The nodes of the schema; `doc` and `text` included. */
	nodes: Record<string, NodeSpec>
	/** The marks, **outermost first**: the record's order is the schema's nesting order. */
	marks: Record<string, MarkSpec>
	tokenizer: TokenizerConfig
	/**
	 * One row per markdown-it token this dialect can produce. The map is *total*: the parser throws
	 * on a token with no row, so a construct the dialect cannot represent fails loudly in a test
	 * instead of being silently dropped out of somebody's text.
	 */
	tokens: Record<string, ParseSpec>
	/**
	 * The writers, over the schema this configuration has just described. `write` serializes a whole
	 * document with this same dialect, for the writer that needs its own output back — a table cell
	 * is markdown inside markdown — and throws while the dialect is still being built.
	 */
	serializer: (schema: Schema, write: (doc: Node) => string) => SerializerSpec
	/**
	 * The blocks markdown writes on a single line, by node name — the blocks a line break cannot
	 * live in. The keymap reads them to keep a break out of a block that would swallow it.
	 */
	oneLineBlocks: string[]
	/** Whether a destination may become a link, an image or an anchor target. */
	allowedHref: (dest: string) => boolean
	/**
	 * The info string a fence may carry, as a regular-expression source: the input rule that makes a
	 * code block out of ` ```go ` needs the same characters inside a larger pattern.
	 */
	fenceInfo: string
}

export interface Dialect {
	name: string
	schema: Schema
	/** The one tokenizer; a second instance configured differently cuts a file differently. */
	tokenizer: MarkdownIt
	parser: MarkdownParser
	tokens: Record<string, ParseSpec>
	oneLineBlocks: readonly NodeType[]
	allowedHref: (dest: string) => boolean
	fenceInfo: string
	/** The document one markdown text describes. */
	parse: (src: string) => Node
	/** The markdown one document stands for. */
	serialize: (doc: Node) => string
}

export function buildDialect(config: DialectConfig): Dialect {
	const schema = new Schema({ nodes: config.nodes, marks: config.marks })
	const tokenizer = buildTokenizer(config.tokenizer)
	const parser = new MarkdownParser(schema, tokenizer, config.tokens)
	// One serializer, reached by its own writers through a closure: a writer that serializes a
	// nested document — a table cell — must run the very rules it was called from, and a second
	// instance built from the same specification would be a second answer waiting to drift.
	let write: (doc: Node) => string = () => {
		throw new Error(`editor: the ${config.name} dialect serialized during its own build`)
	}
	const serialize = buildSerializer(config.serializer(schema, (doc) => write(doc)))
	write = serialize
	return {
		name: config.name,
		schema,
		tokenizer,
		parser,
		tokens: config.tokens,
		oneLineBlocks: config.oneLineBlocks.map((name) => nodeType(schema, config.name, name)),
		allowedHref: config.allowedHref,
		fenceInfo: config.fenceInfo,
		parse: (src) => parser.parse(src),
		serialize,
	}
}

export { markType, nodeType } from "../schemaTypes.ts"
export { hrefChecker, type HrefRule, type PathRule } from "./href.ts"
export { baseMarks, type MarkOptions } from "./marks.ts"
export { baseNodes, decorativeTaskBox, type NodeOptions } from "./nodes.ts"
export {
	buildSerializer,
	fenceFor,
	sharedMarkWriters,
	sharedNodeWriters,
	withoutBreaks,
	type MarkWriter,
	type NodeWriter,
	type SerializerSpec,
	type WritableState,
} from "./serialize.ts"
export { buildTokenizer, taskListRule, type CoreRule, type TokenizerConfig } from "./tokenizer.ts"
export type { ParseSpec }
